import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import BookmarkAddOutlinedIcon from '@mui/icons-material/BookmarkAddOutlined';
import { TEMPLATES } from '../constants';
import type { Dictionary } from '../i18n';
import type { EditorState, Language, StylePreset } from '../types';

interface Props {
  t: Dictionary;
  language: Language;
  onApplyTemplate: (patch: Partial<EditorState>) => void;
  presets: StylePreset[];
  onApplyPreset: (preset: StylePreset) => void;
  onSavePreset: (name: string) => void;
  onDeletePreset: (id: string) => void;
}

export function PresetBar({
  t,
  language,
  onApplyTemplate,
  presets,
  onApplyPreset,
  onSavePreset,
  onDeletePreset,
}: Props) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState('');

  const submit = () => {
    onSavePreset(name);
    setName('');
    setDialogOpen(false);
  };

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {t.templates}
        </Typography>
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
          {TEMPLATES.map((template) => (
            <Chip
              key={template.id}
              label={template.name[language]}
              onClick={() => onApplyTemplate(template.patch)}
              variant="outlined"
              color="primary"
            />
          ))}
        </Stack>
      </Box>

      <Box>
        <Stack
          direction="row"
          sx={{ mb: 1, alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Typography variant="body2">{t.presets}</Typography>
          <Button
            size="small"
            startIcon={<BookmarkAddOutlinedIcon />}
            onClick={() => setDialogOpen(true)}
          >
            {t.savePreset}
          </Button>
        </Stack>
        {presets.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            {t.noPresets}
          </Typography>
        ) : (
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
            {presets.map((preset) => (
              <Chip
                key={preset.id}
                label={preset.name}
                onClick={() => onApplyPreset(preset)}
                onDelete={() => onDeletePreset(preset.id)}
              />
            ))}
          </Stack>
        )}
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t.savePreset}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label={t.presetNamePrompt}
            value={name}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && submit()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="inherit">
            {t.cancel}
          </Button>
          <Button onClick={submit} variant="contained">
            {t.save}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
